// src/utils/pdfUtils.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoBase64 from "assets/greenland-name-logo-base64";

const SOURCE = "Source: Greenland Risk Management LLC";
const DISCLAIMER =
  "This information provided for informational purposes only and does not constitute trading, investment, accounting, legal or tax advice. This information does not constitute a recommendation, solicitation or municipal advisory service, and access does not create an advisory or fiduciary relationship. Forward-looking statements involve risks and uncertainties and results may differ from past performance. See more information at greenlandrm.com/disclosures.";
const MEMBERSHIP = "Member NFA | SEC/MSRB Registered";
const COPYRIGHT = `©${new Date().getFullYear()} Greenland Risk Management LLC. All rights reserved.`;

/**
 * Create a branded jsPDF instance with header/footer
 * @param {string} asOfDate - Market data date string (m/d/yyyy or ISO)
 * @param {string} createdStamp - Already formatted display string for "Report created ..."
 * @param {string} orgName - Organization name to display in header
 * @param {object} opts
 * @param {string} [opts.reportTitle] - Override header title (defaults to TradeTracker title)
 * @param {string} [opts.asOfLabel] - Override as-of line (defaults to "Market data as of ... end of day")
 */
export function createBrandedPDF(asOfDate, createdStamp, orgName = "", opts = {}) {
  const { reportTitle, asOfLabel } = opts;

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // --- Header ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(58, 89, 24);
  doc.text(reportTitle || "Fair Market Value Report", 40, 50);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(60);
  doc.text(orgName || "—", 40, 72);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(
    asOfLabel || `Market data as of ${asOfDate} end of day`,
    40,
    88
  );

  // --- Logo ---
  try {
    doc.addImage(logoBase64, "PNG", pageWidth - 180, 40, 140, 0);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to render Greenland logo in PDF header:", e);
  }

  // --- Footer factory ---
  const addFooter = (_pageNum, _totalPages) => {
    const footerTop = pageHeight - 110;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120);

    doc.text(SOURCE, 40, footerTop, { align: "left" });
    doc.text(DISCLAIMER, 40, footerTop + 15, { maxWidth: pageWidth - 80 });

    doc.text(MEMBERSHIP, 40, footerTop + 60, { align: "left" });

    doc.text(COPYRIGHT, 40, footerTop + 75, { align: "left" });
    doc.text(`Report created ${createdStamp}`, pageWidth - 40, footerTop + 75, {
      align: "right",
    });
  };

  return { doc, addFooter };
}

export function addBrandedTable(doc, head, body, totals, startY = 140) {
  const extendedBody = [
    ...body,
    [
      "TOTAL",
      "",
      "",
      "",
      "",
      "",
      "",
      totals.totalNotional || "–",
      totals.totalValue || "–",
    ],
  ];

  autoTable(doc, {
    head: [head],
    body: extendedBody,
    startY,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: {
      fillColor: [94, 140, 62],
      textColor: [255, 255, 255],
      halign: "center",
    },
    columnStyles: {
      7: { halign: "right" },
      8: { halign: "right" },
    },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    bodyStyles: { valign: "middle" },
    didParseCell: (data) => {
      if (data.row.index === extendedBody.length - 1) {
        data.cell.styles.fillColor = [230, 230, 230];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });
}
